<template>
    <DCard
        :title="__('customers.business_incidents.connected_invoices.title', {numberOfItems: items.length})"
        :main-title-has-slot="true"
        padding
    >
        <template #titleHasSlot>
            <DSwitch
                color="success"
                variant="3d"
                :disabled="isInLoadingState"
                :checked="showCardData"
                @update:checked="showCardSection"
            />
        </template>
        <DRow v-if="showCardData">
            <DCol col="6">
                <p>
                    <small class="text-muted">{{ __('customers.business_incidents.connected_invoices.hint') }}</small>
                </p>
                <section class="invoices-table">
                    <div class="table-header">
                        <div>{{ __('customers.business_incidents.connected_invoices.invoice_id') }}</div>
                        <div>{{ __('customers.business_incidents.connected_invoices.total_amount') }}</div>
                        <div>{{ __('customers.business_incidents.connected_invoices.status') }}</div>
                        <div>
                            <DButton
                                v-if="userPermissions.includes('customer.incidents_manage') && incident.status === 'open'"
                                id="add"
                                icon="plus"
                                mini
                                :disabled="isInLoadingState"
                                @click="addInvoices"
                            />
                        </div>
                    </div>
                    <main>
                        <div
                            v-for="(item, index) in items"
                            :key="index"
                            class="item-group"
                        >
                            <div class="item-header">
                                <div>
                                    <Link :href="invoiceRoute(item)">
                                        {{ item.invoice_id }}
                                    </Link>
                                </div>
                                <div>
                                    {{ priceFormat(item.total_amount, 2, customer.market_region_id) }}
                                </div>
                                <div>
                                    {{ __('customers.contacts.invoice_status.' + item.status) }}
                                </div>
                                <div>
                                    <DButton
                                        v-if="userPermissions.includes('customer.incidents_manage') && incident.status === 'open'"
                                        :id="`delete_invoice_${item.id}`"
                                        :disabled="isInLoadingState"
                                        icon="trash-can-outline"
                                        color="danger"
                                        mini
                                        @click="showRemoveItem(item)"
                                    />
                                </div>
                            </div>
                        </div>
                    </main>
                </section>
            </DCol>
        </DRow>
        <AddItems
            v-if="showAddInvoiceModal"
            :show-modal="showAddInvoiceModal"
            :incident="incident"
            :title="__('customers.business_incidents.connected_invoices.modal.add.title')"
            :label="__('customers.business_incidents.connected_invoices.modal.add.label')"
            :section="'invoices'"
            @close-modal="closeAddModal"
            @confirm-modal="save"
        />
        <RemoveItem
            v-if="showRemoveItemModal"
            :show-modal="showRemoveItemModal"
            :title="__('customers.business_incidents.connected_invoices.modal.remove.title')"
            :message="__('customers.business_incidents.connected_invoices.modal.remove.message', {invoice: invoiceId})"
            @close-modal="closeRemoveModal"
            @confirm-modal="remove"
        />
    </DCard>
</template>

<script setup>
import { route } from '~@/Composables/route'
import { computed, ref } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import RemoveItem from '~@/Components/Customers/BusinessIncidents/RemoveItem'
import AddItems from '~@/Components/Customers/BusinessIncidents/AddItems'

const props = defineProps({
    items: {
        type: Array,
        default () {
            return []
        }
    },
    incident: {
        type: Object,
        default () {
            return {}
        }
    },
    showCard: {
        type: Boolean,
        default () {
            return true
        }
    },
    customer: {
        type: Object,
        default: () => {
            return {}
        }
    }
})

const emit = defineEmits(['addInvoices'])

const showCardData = ref(props.showCard)
const showRemoveItemModal = ref(false)
const showAddInvoiceModal = ref(false)
const invoiceId = ref(null)
const businessIncidentInvoiceId = ref(null)
const userPermissions = computed(() => usePage().props.auth.permissions)

function showCardSection (value) {
    showCardData.value = value
}

function invoiceRoute (item) {
    return route('customers.customers.invoices.show', { customer: props.incident.customer_id, invoice: item.invoice_id })
}

function showRemoveItem (item) {
    businessIncidentInvoiceId.value = item.id
    invoiceId.value = item.invoice_id
    showRemoveItemModal.value = true
}

function addInvoices () {
    showAddInvoiceModal.value = true
}

function closeRemoveModal () {
    businessIncidentInvoiceId.value = null
    invoiceId.value = null
    showRemoveItemModal.value = false
}

function remove () {
    router.delete(route('customers.customers.delete-business-incident-invoice', { businessIncidentInvoice: businessIncidentInvoiceId.value }))
    showRemoveItemModal.value = false
}

function closeAddModal () {
    showAddInvoiceModal.value = false
}

function save (form) {
    emit('addInvoices', form)

    showAddInvoiceModal.value = false
}

</script>

<style lang="scss" scoped>
.invoices-table {
    & .table-header {
        border-bottom: 1px solid rgba(0,0,0,0.14);
        display: grid;
        grid-template-columns: 25% 25% 30% 20%;
        grid-gap: 10px;
        align-items: center;

        & > div {
            padding: 10px;
            font-weight: bold;
        }
    }
    & main {
        & .item-group {
            border-bottom: 1px solid rgba(0,0,0,0.3);
            position: relative;

            & .item-header {
                display: grid;
                grid-template-columns: 25% 25% 30% 20%;
                grid-gap: 10px;
                background: #fff;
                border-bottom: 1px solid rgba(0,0,0,0.14);
                position: sticky;
                top: -25px;
                width: 100%;
                z-index: 100;

                & > div {
                    padding: 10px;
                    align-self: center;
                }
            }
        }
    }
}
</style>
